import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { SignatureModal } from './SignatureModal';
import { VistaPreviaDocumento } from './VistaPreviaDocumento';
import { toast } from 'sonner';
import {
  FileText, Plus, Download, MessageCircle, Mail, PenLine, Archive,
  History, ChevronUp, Search, Pencil, Trash2, FileCheck, Stamp, Check
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADO_STYLES = {
  borrador: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
  generado: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  pendiente_firma: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  firmado: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  enviado: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  archivado: 'bg-zinc-700/30 text-zinc-500 border-zinc-600/30',
};

const ACCION_ICONS = {
  borrador: 'bg-zinc-500', editado: 'bg-zinc-400', generado: 'bg-sky-400',
  enviado_whatsapp: 'bg-indigo-400', enviado_email: 'bg-indigo-400',
  firmado: 'bg-emerald-400', archivado: 'bg-zinc-600',
};

function SeleccionModal({ trialId, plantillas, onClose, onContinuar }) {
  const [selected, setSelected] = useState(null);
  const [clienteQuery, setClienteQuery] = useState('');
  const [clientes, setClientes] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);

  const buscarClientes = async (q) => {
    setClienteQuery(q);
    setClienteSel(null);
    if (q.length < 2) { setClientes([]); return; }
    try {
      const r = await axios.get(`${API_URL}/api/comercial/buscar`, { params: { q }, withCredentials: true });
      setClientes(r.data.results || []);
    } catch { setClientes([]); }
  };

  const handleContinuar = () => {
    const tid = trialId || clienteSel?.id;
    if (!selected) { toast.error('Selecciona una plantilla'); return; }
    if (!tid) { toast.error('Selecciona un cliente'); return; }
    onContinuar({ trialId: tid, templateCodigo: selected });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] font-light text-xl">Nuevo Documento</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!trialId && (
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Cliente</label>
              {clienteSel ? (
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <span className="text-sm text-emerald-300">{clienteSel.empresa || clienteSel.nombre_contacto} · {clienteSel.client_code}</span>
                  <button onClick={() => setClienteSel(null)} className="text-xs text-zinc-400 hover:text-white">Cambiar</button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      value={clienteQuery}
                      onChange={(e) => buscarClientes(e.target.value)}
                      placeholder="Buscar cliente por nombre, código, teléfono..."
                      className="pl-9 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
                      data-testid="doc-buscar-cliente-input"
                    />
                  </div>
                  {clientes.length > 0 && (
                    <div className="mt-2 rounded-lg border border-white/10 divide-y divide-white/[0.06] max-h-40 overflow-y-auto">
                      {clientes.map((c) => (
                        <button key={c.id} onClick={() => setClienteSel(c)}
                          className="w-full p-2.5 text-left text-sm text-zinc-300 hover:bg-white/[0.05]"
                          data-testid={`doc-cliente-opcion-${c.client_code}`}>
                          {c.empresa || c.nombre_contacto} <span className="text-xs text-zinc-500 font-mono">{c.client_code}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <label className="text-xs text-zinc-500 mb-1.5 block">Plantilla oficial</label>
            <div className="space-y-2">
              {plantillas.map((p) => (
                <button
                  key={p.codigo}
                  onClick={() => setSelected(p.codigo)}
                  className={`w-full p-3 rounded-xl border text-left transition-colors ${
                    selected === p.codigo
                      ? 'bg-sky-500/15 border-sky-500/40'
                      : 'bg-white/[0.02] border-white/10 hover:border-white/25'
                  }`}
                  data-testid={`doc-plantilla-${p.codigo}`}
                >
                  <p className={`text-sm font-medium ${selected === p.codigo ? 'text-sky-300' : 'text-white'}`}>{p.nombre}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{p.descripcion}</p>
                </button>
              ))}
            </div>
          </div>

          <p className="text-[10px] text-zinc-600">
            En el siguiente paso podrás revisar y editar los datos autorrellenados antes de generar el documento.
          </p>

          <Button onClick={handleContinuar} className="w-full bg-white text-black hover:bg-zinc-200" data-testid="doc-continuar-preview-btn">
            Continuar a vista previa
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Timeline({ historial }) {
  return (
    <div className="relative pl-4">
      <div className="absolute left-[5px] top-1.5 bottom-1.5 w-px bg-white/10" />
      <div className="space-y-2.5">
        {(historial || []).map((h, i) => (
          <div key={i} className="relative">
            <span className={`absolute -left-4 top-1 w-[11px] h-[11px] rounded-full border-2 border-[#0a0a0a] ${ACCION_ICONS[h.accion] || 'bg-zinc-500'}`} />
            <p className="text-xs text-zinc-400 pl-1">
              <span className="text-zinc-600 font-mono">{h.fecha?.slice(0, 16).replace('T', ' ')}</span>
              {' · '}{h.nota} <span className="text-zinc-600">({h.usuario})</span>
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FirmantesChips({ d }) {
  if (!d.firmas_requeridas?.length || d.estado === 'borrador') return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {d.firmas_requeridas.map((f) => (
        <span key={f.rol} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium border ${
          f.firmado
            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
            : 'bg-amber-500/10 text-amber-400 border-amber-500/25'
        }`} data-testid={`doc-firmante-chip-${d.id}-${f.rol}`}>
          {f.firmado ? <Check className="w-2.5 h-2.5" /> : (f.sello_automatico ? <Stamp className="w-2.5 h-2.5" /> : <PenLine className="w-2.5 h-2.5" />)}
          {f.rol}{f.firmado ? '' : ' · pendiente'}
        </span>
      ))}
    </div>
  );
}

export function DocumentosCliente({ trialId = null, showCliente = false }) {
  const [documentos, setDocumentos] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSeleccion, setShowSeleccion] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editando, setEditando] = useState(null);
  const [firmando, setFirmando] = useState(null);
  const [historialOpen, setHistorialOpen] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = trialId ? { trial_id: trialId } : {};
      const [docsRes, plRes] = await Promise.all([
        axios.get(`${API_URL}/api/documentos`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/documentos/plantillas`, { withCredentials: true }),
      ]);
      setDocumentos(docsRes.data.documentos || []);
      setPlantillas(plRes.data.plantillas || []);
    } catch (err) {
      console.error('Error cargando documentos:', err);
    } finally {
      setLoading(false);
    }
  }, [trialId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const verPdf = (d) => window.open(`${API_URL}/api/documentos/${d.id}/pdf`, '_blank');

  const editar = async (d) => {
    try {
      const r = await axios.get(`${API_URL}/api/documentos/${d.id}`, { withCredentials: true });
      setEditando(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al abrir el borrador');
    }
  };

  const generarBorrador = async (d) => {
    try {
      await axios.post(`${API_URL}/api/documentos/${d.id}/generar`, {}, { withCredentials: true });
      toast.success('Documento generado · pendiente de firma');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar');
    }
  };

  const eliminar = async (d) => {
    if (!window.confirm(`¿Eliminar el borrador "${d.template_nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API_URL}/api/documentos/${d.id}`, { withCredentials: true });
      toast.success('Borrador eliminado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const enviar = async (d, canal) => {
    try {
      const r = await axios.post(`${API_URL}/api/documentos/${d.id}/enviar`, { canal }, { withCredentials: true });
      if (canal === 'whatsapp' && r.data.whatsapp_url) window.open(r.data.whatsapp_url, '_blank');
      toast.success(r.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
    }
  };

  const firmar = async (payload) => {
    try {
      const r = await axios.post(`${API_URL}/api/documentos/${firmando.id}/firmar`, payload, { withCredentials: true });
      toast.success(r.data.estado === 'firmado' ? 'Documento completamente firmado' : 'Firma registrada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al firmar');
      throw err;
    }
  };

  const archivar = async (d) => {
    try {
      await axios.post(`${API_URL}/api/documentos/${d.id}/archivar`, {}, { withCredentials: true });
      toast.success('Documento archivado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al archivar');
    }
  };

  return (
    <div data-testid="documentos-modulo">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-zinc-500">{documentos.length} documento(s)</p>
        <Button
          size="sm"
          onClick={() => setShowSeleccion(true)}
          className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
          data-testid="doc-generar-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" />Nuevo documento
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500 py-6 text-center">Cargando...</p>
      ) : documentos.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">Sin documentos generados</p>
          <p className="text-xs text-zinc-600 mt-1">Crea el primer documento con vista previa editable</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documentos.map((d) => (
            <div key={d.id} className="rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid={`doc-item-${d.id}`}>
              <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white font-medium">{d.template_nombre}</span>
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${ESTADO_STYLES[d.estado] || ESTADO_STYLES.generado}`} data-testid={`doc-estado-${d.id}`}>
                      {d.estado_label}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    {showCliente && d.cliente_nombre ? `${d.cliente_nombre} (${d.client_code}) · ` : ''}
                    Creado: {d.created_at?.slice(0, 10)}
                    {d.fecha_firma ? ` · Firmado: ${d.fecha_firma.slice(0, 10)}` : ''}
                    {' · '}Creado por: {d.creado_por?.nombre || d.responsable_nombre}
                  </p>
                  <FirmantesChips d={d} />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={() => verPdf(d)} className="border-white/20 text-zinc-300 h-8" data-testid={`doc-pdf-${d.id}`}>
                    <Download className="w-3.5 h-3.5 mr-1" />PDF
                  </Button>
                  {d.estado === 'borrador' && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => editar(d)} className="border-sky-500/30 text-sky-400 h-8" data-testid={`doc-editar-${d.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" />Editar
                      </Button>
                      <Button size="sm" onClick={() => generarBorrador(d)} className="bg-white/10 text-white border border-white/20 h-8" data-testid={`doc-generar-borrador-${d.id}`}>
                        <FileCheck className="w-3.5 h-3.5 mr-1" />Generar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => eliminar(d)} className="border-red-500/30 text-red-400 h-8" data-testid={`doc-eliminar-${d.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                  {d.estado === 'pendiente_firma' && (
                    <>
                      <Button size="sm" onClick={() => enviar(d, 'whatsapp')} className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 h-8" data-testid={`doc-whatsapp-${d.id}`}>
                        <MessageCircle className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" onClick={() => enviar(d, 'email')} className="bg-sky-500/20 text-sky-400 border border-sky-500/30 h-8" data-testid={`doc-email-${d.id}`}>
                        <Mail className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" onClick={() => setFirmando(d)} className="bg-amber-500/20 text-amber-400 border border-amber-500/30 h-8" data-testid={`doc-firmar-${d.id}`}>
                        <PenLine className="w-3.5 h-3.5 mr-1" />Firmar
                      </Button>
                    </>
                  )}
                  {d.estado === 'firmado' && (
                    <>
                      <Button size="sm" onClick={() => enviar(d, 'whatsapp')} className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 h-8" data-testid={`doc-whatsapp-${d.id}`}>
                        <MessageCircle className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" onClick={() => enviar(d, 'email')} className="bg-sky-500/20 text-sky-400 border border-sky-500/30 h-8" data-testid={`doc-email-${d.id}`}>
                        <Mail className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => archivar(d)} className="border-white/10 text-zinc-500 h-8" data-testid={`doc-archivar-${d.id}`}>
                        <Archive className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                  {d.estado === 'enviado' && (
                    <Button variant="outline" size="sm" onClick={() => archivar(d)} className="border-white/10 text-zinc-500 h-8" data-testid={`doc-archivar-${d.id}`}>
                      <Archive className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <button
                    onClick={() => setHistorialOpen(historialOpen === d.id ? null : d.id)}
                    className="p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-white/[0.06]"
                    data-testid={`doc-historial-${d.id}`}
                  >
                    {historialOpen === d.id ? <ChevronUp className="w-4 h-4" /> : <History className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {historialOpen === d.id && (
                <div className="px-4 pb-4 border-t border-white/[0.06] pt-3">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2.5">Historial del documento</p>
                  <Timeline historial={d.historial} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showSeleccion && (
        <SeleccionModal
          trialId={trialId}
          plantillas={plantillas}
          onClose={() => setShowSeleccion(false)}
          onContinuar={(sel) => { setShowSeleccion(false); setPreview(sel); }}
        />
      )}
      {preview && (
        <VistaPreviaDocumento
          trialId={preview.trialId}
          templateCodigo={preview.templateCodigo}
          onClose={() => setPreview(null)}
          onSaved={fetchData}
        />
      )}
      {editando && (
        <VistaPreviaDocumento
          documento={editando}
          onClose={() => setEditando(null)}
          onSaved={fetchData}
        />
      )}
      {firmando && (
        <SignatureModal documento={firmando} onClose={() => setFirmando(null)} onSign={firmar} />
      )}
    </div>
  );
}
